// AdminUserManagement.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch,
    faEye,
    faEyeSlash,
    faEdit,
    faCircleCheck,
    faCircleXmark,
    faLock,
    faUsers,
    faWallet,
    faSpinner,
    faDownload,
    faUserPlus,
    faChevronLeft,
    faChevronRight,
    faFileCsv,
    faFileExcel,
    faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import {
    Row,
    Col,
    Card,
    Table,
    InputGroup,
    Form,
    Button,
    Badge,
    Modal,
} from "@themesberg/react-bootstrap";
import { listOfUsers, changeUserStatus, blockUserStatus, adminChangeUserPassword, adminExportUserReport } from "../../api";
import { Routes } from "../../../routes";

const ACTIVE_BUTTON_STYLE = {
    background: "#1D4E89",
    border: "none",
    borderRadius: "0.5rem",
    color: "#ffffff",
};

const statusOptions = [
    { value: "", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "blocked", label: "Blocked" },
];

const getStatusBadgeStyle = (status) => {
    switch (String(status || "").toLowerCase()) {
        case "active":
            return { background: "#10b981", color: "#ffffff" };
        case "inactive":
            return { background: "#f59e0b", color: "#ffffff" };
        case "blocked":
            return { background: "#ef4444", color: "#ffffff" };
        default:
            return { background: "#6b7280", color: "#ffffff" };
    }
};

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const normalizeListUser = (item) => ({
    id: item?.id ?? "",
    name: item?.name ?? "",
    email: item?.email ?? "",
    mobile: item?.mobile_number ?? "",
    referral_number: item?.referral_number ?? "",
    userType: item?.user_type ?? "",
    status: item?.status ?? "",
});

const getPasswordRules = (password) => {
    const value = String(password || "");
    return {
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /\d/.test(value),
        special: /[@$!%*?&]/.test(value),
        minLength: value.length >= 8,
    };
};

const isPasswordValid = (password) => {
    const rules = getPasswordRules(password);
    return Object.values(rules).every(Boolean);
};

const getPasswordError = (password) => {
    if (!String(password || "").trim()) return "Password is required.";
    if (!isPasswordValid(password)) return "Password must meet all the rules below.";
    return "";
};

const getConfirmPasswordError = (password, confirmPassword) => {
    const confirm = String(confirmPassword || "");
    if (!confirm.trim()) return "Confirm password is required.";
    if (String(password || "") !== confirm) return "Passwords do not match.";
    return "";
};

export default function AdminUserManagement() {
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportingFormat, setExportingFormat] = useState(null);

    const searchDebounceRef = useRef(null);
    const initialLoadRef = useRef(true);

    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedPasswordUser, setSelectedPasswordUser] = useState(null);
    const [passwordSubmitting, setPasswordSubmitting] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        password: "",
        confirmPassword: "",
    });
    const [passwordErrors, setPasswordErrors] = useState({
        password: "",
        confirmPassword: "",
    });
    const [passwordTouched, setPasswordTouched] = useState({
        password: false,
        confirmPassword: false,
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const getAdminUserDetailsPath = (userId) => {
        return Routes?.AdminUserDetails?.path?.replace(":id", userId) || `/admin-user-details/${userId}`;
    };

    const getAdminAddUserPath = () => {
        return Routes?.AdminAddUser?.path || "/admin-add-user";
    };

    const getAdminEditUserPath = (userId) => {
        return Routes?.AdminEditUser?.path?.replace(":id", userId) || `/admin-edit-user/${userId}`;
    };

    // const handleExport = () => {
    //     toast.info("Export action clicked.");
    // };

    const buildExportParams = () => {
        const params = {};

        const trimmedSearch = searchTerm.trim();
        const trimmedStatus = statusFilter.trim();

        if (trimmedSearch) params.search = trimmedSearch;
        if (trimmedStatus) params.status = trimmedStatus;

        return params;
    };

    const downloadBlobFile = (blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
    };

    const getExportFileName = (format) => {
        const safeFormat = String(format || "xlsx").toLowerCase();

        if (safeFormat === "csv") return "users-report.csv";
        if (safeFormat === "pdf") return "users-report.pdf";
        return "users-report.xlsx";
    };

    const handleExport = async (format) => {
        try {
            setExportingFormat(format);

            const params = buildExportParams();
            params.format = format;

            const response = await adminExportUserReport(params);
            const blob = response?.data;

            if (!blob) {
                toast.error("Export file not received.");
                return;
            }

            downloadBlobFile(blob, getExportFileName(format));
            toast.success("Report exported successfully.");
        } catch (error) {
            console.error("Export failed:", error);
            toast.error(error?.response?.data?.message || "Unable to export report.");
        } finally {
            setExportingFormat(null);
        }
    };


    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });

    const clearSearchDebounce = () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
    };

    const fetchUsers = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: 10,
            };

            const trimmedSearch = searchTerm.trim();
            const trimmedStatus = statusFilter.trim();

            if (trimmedSearch) {
                params.search = trimmedSearch;
            }

            if (trimmedStatus) {
                params.status = trimmedStatus;
            }

            const res = await listOfUsers(params);

            const responseData = res?.data?.data ?? res?.data ?? {};
            const apiUsers = Array.isArray(responseData)
                ? responseData
                : Array.isArray(responseData?.users)
                    ? responseData.users
                    : Array.isArray(responseData?.data)
                        ? responseData.data
                        : [];

            const apiPagination = Array.isArray(responseData)
                ? res?.data?.pagination || {}
                : responseData?.pagination || res?.data?.pagination || {};

            setUsers(apiUsers.map(normalizeListUser));
            setPagination({
                current_page: Number(apiPagination.current_page) || page,
                last_page: Number(apiPagination.last_page) || 1,
                per_page: Number(apiPagination.per_page) || 10,
                total: Number(apiPagination.total) || apiUsers.length,
            });
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to fetch users.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            fetchUsers(1);
            return;
        }

        clearSearchDebounce();
        searchDebounceRef.current = setTimeout(() => {
            fetchUsers(1);
        }, 300);

        return () => {
            clearSearchDebounce();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, statusFilter]);

    const filteredUsers = users;

    const getShowingRange = () => {
        const total = pagination.total || 0;
        const current = pagination.current_page || 1;
        const perPage = pagination.per_page || 10;

        if (total === 0) {
            return { start: 0, end: 0 };
        }

        const start = (current - 1) * perPage + 1;
        const end = Math.min(current * perPage, total);

        return { start, end };
    };

    const getPageItems = () => {
        const totalPages = pagination.last_page || 1;
        const current = pagination.current_page || 1;

        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        // Start pages
        if (current <= 2) {
            return [1, 2, 3, "...", totalPages];
        }

        // End pages
        if (current >= totalPages - 1) {
            return [1, "...", totalPages - 2, totalPages - 1, totalPages];
        }

        // Middle pages: 1 ... previous current next ... last
        return [1, "...", current - 1, current, current + 1, "...", totalPages];
    };

    const { start, end } = getShowingRange();

    // const summary = useMemo(() => {
    //     return {
    //         totalUsers: pagination.total || users.length,
    //         activeUsers: users.filter((u) => String(u.status).toLowerCase() === "active").length,
    //         blockedUsers: users.filter((u) => String(u.status).toLowerCase() === "blocked").length,
    //         totalWallet: 0,
    //     };
    // }, [users, pagination.total]);

    const handleDelete = (user) => {
        const confirmDelete = window.confirm(`Delete ${user.name}?`);
        if (!confirmDelete) return;
        toast.info(`Delete API will be added for ${user.name}.`);
    };

    const updateUserStatusInState = (userId, nextStatus) => {
        setUsers((prev) =>
            prev.map((item) =>
                String(item.id) === String(userId) ? { ...item, status: nextStatus } : item
            )
        );
    };

    const formatStatus = (value) => {
        if (!value) return "N/A" || "-";
        const str = String(value).trim();
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const handleStatusChange = async (user, nextStatus) => {
        const currentStatus = String(user?.status || "").toLowerCase();
        const next = String(nextStatus || "").toLowerCase();

        if (!user?.id) return;

        const confirmChange = window.confirm(
            `Are you sure you want to change status from "${formatStatus(currentStatus) || "N/A" || "-"}" to "${formatStatus(next) || "N/A" || "-"}" for ${user.name}?`
        );

        if (!confirmChange) return;

        try {
            const payload = {
                user_id: user.id,
                status: next,
            };

            const response = await changeUserStatus(payload);
            const responseData = response?.data || {};

            if (responseData?.success === false) {
                toast.error(responseData?.message || "Unable to change user status.");
                return;
            }

            updateUserStatusInState(user.id, next);
            await fetchUsers(pagination.current_page || 1);
            toast.success(responseData?.message || "User status updated successfully.");
        } catch (error) {
            console.error("Status change failed:", error);
            toast.error(error?.response?.data?.message || "Unable to change user status.");
        }
    };

     const handleBlockUserStatus = async (user) => {

        if (!user?.id) return;

        // const confirmChange = window.confirm(
        //     `Are you sure you want to change status from "${formatStatus(currentStatus) || "N/A" || "-"}" to "${formatStatus(next) || "N/A" || "-"}" for ${user.name}?`
        // );

        const confirmChange = window.confirm(
            `Are you sure you want to block ${user.name}?`
        );

        if (!confirmChange) return;

        try {
            const payload = {
                user_id: user.id,
            };

            const response = await blockUserStatus(payload);
            const responseData = response?.data || {};

            if (responseData?.success === false) {
                toast.error(responseData?.message || "Unable to block user.");
                return;
            }

            // updateUserStatusInState(user.id, next);
            await fetchUsers(pagination.current_page || 1);
            toast.success(responseData?.message || "User blocked successfully.");
        } catch (error) {
            console.error("Status change failed:", error);
            toast.error(error?.response?.data?.message || "Unable to block user.");
        }
    };

    const handleInlineStatusToggle = (user) => {
        const currentStatus = String(user?.status || "").toLowerCase();
        const nextStatus = currentStatus === "active" ? "inactive" : "active";
        handleStatusChange(user, nextStatus);
    };

    // const handleBlockUser = (user) => {
    //     const currentStatus = String(user?.status || "").toLowerCase();
    //     const nextStatus = currentStatus === "blocked" ? "active" : "blocked";
    //     handleStatusChange(user, nextStatus);
    // };

    const handleBlockUser = (user) => {
        handleBlockUserStatus(user);
    };


    const openChangePasswordModal = (user) => {
        setSelectedPasswordUser(user);
        setPasswordForm({
            password: "",
            confirmPassword: "",
        });
        setPasswordErrors({
            password: "",
            confirmPassword: "",
        });
        setPasswordTouched({
            password: false,
            confirmPassword: false,
        });
        setShowPassword(false);
        setShowConfirmPassword(false);
        setShowPasswordModal(true);
    };

    const closeChangePasswordModal = () => {
        setShowPasswordModal(false);
        setSelectedPasswordUser(null);
        setPasswordForm({
            password: "",
            confirmPassword: "",
        });
        setPasswordErrors({
            password: "",
            confirmPassword: "",
        });
        setPasswordTouched({
            password: false,
            confirmPassword: false,
        });
        setShowPassword(false);
        setShowConfirmPassword(false);
        setPasswordSubmitting(false);
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;

        setPasswordForm((prev) => {
            const nextForm = { ...prev, [name]: value };

            setPasswordErrors((prevErrors) => {
                const nextErrors = { ...prevErrors };

                if (name === "password") {
                    nextErrors.password = getPasswordError(value);

                    if (passwordTouched.confirmPassword) {
                        nextErrors.confirmPassword = getConfirmPasswordError(value, nextForm.confirmPassword);
                    } else {
                        nextErrors.confirmPassword = "";
                    }
                }

                if (name === "confirmPassword") {
                    nextErrors.confirmPassword = getConfirmPasswordError(nextForm.password, value);
                }

                return nextErrors;
            });

            return nextForm;
        });

        setPasswordTouched((prev) => ({
            ...prev,
            [name]: true,
        }));
    };

    const validatePasswordForm = () => {
        const nextErrors = {
            password: getPasswordError(passwordForm.password),
            confirmPassword: getConfirmPasswordError(passwordForm.password, passwordForm.confirmPassword),
        };

        setPasswordErrors(nextErrors);
        setPasswordTouched({
            password: true,
            confirmPassword: true,
        });

        return !nextErrors.password && !nextErrors.confirmPassword;
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (!validatePasswordForm()) {
            toast.error("Please correct the highlighted fields.");
            return;
        }

        if (!selectedPasswordUser?.id) {
            toast.error("Selected user is missing.");
            return;
        }

        try {
            setPasswordSubmitting(true);

            const payload = {
                password: passwordForm.password.trim(),
            };

            const response = await adminChangeUserPassword(selectedPasswordUser.id, payload);
            const responseData = response?.data || {};

            if (responseData?.success === false) {
                toast.error(responseData?.message || "Unable to change password.");
                return;
            }

            toast.success(responseData?.message || "Password changed successfully.");
            closeChangePasswordModal();
        } catch (error) {
            console.error("Change password failed:", error);
            toast.error(error?.response?.data?.message || "Unable to change password.");
        } finally {
            setPasswordSubmitting(false);
        }
    };

    const passwordRules = useMemo(
        () => getPasswordRules(passwordForm.password),
        [passwordForm.password]
    );

    const unmetPasswordRules = useMemo(() => {
        const rules = [
            { key: "uppercase", label: "At least 1 uppercase letter" },
            { key: "lowercase", label: "At least 1 lowercase letter" },
            { key: "number", label: "At least 1 number" },
            { key: "special", label: "At least 1 special character (@$!%*?&)" },
            { key: "minLength", label: "Minimum 8 characters" },
        ];

        return rules.filter((rule) => !passwordRules[rule.key]);
    }, [passwordRules]);

    const showPasswordError = passwordTouched.password && !!passwordErrors.password;
    const showConfirmError = passwordTouched.confirmPassword && !!passwordErrors.confirmPassword;

    return (
        <>
            <div className="admin-user-header py-4">
                <div className="admin-user-header-title">
                    {/* <h5 className="mb-0">User Management</h5> */}
                    <h2 className="h4 mb-1">User Management</h2>
                </div>

                <div className="admin-user-header-actions">
                    {/* <Button type="button" onClick={handleExport} variant="outline-primary">
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        Export
                    </Button>*/}

                    <Button type="button" onClick={() => navigate(getAdminAddUserPath())} style={ACTIVE_BUTTON_STYLE}>
                        <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                        Add User
                    </Button>
                </div>
            </div>

            {/* <Row className="g-3 mb-4">
                <Col xs={12} sm={6} xl={3}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <Card.Body className="p-4 d-flex justify-content-between">
                            <div>
                                <div className="text-muted small">Total Users</div>
                                <h3 className="mb-0">{summary.totalUsers}</h3>
                            </div>
                            <div className="icon-box">
                                <FontAwesomeIcon icon={faUsers} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} xl={3}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <Card.Body className="p-4 d-flex justify-content-between">
                            <div>
                                <div className="text-muted small">Active Users</div>
                                <h3 className="mb-0">{summary.activeUsers}</h3>
                            </div>
                            <div className="icon-box">
                                <FontAwesomeIcon icon={faCircleCheck} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} xl={3}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <Card.Body className="p-4 d-flex justify-content-between">
                            <div>
                                <div className="text-muted small">Blocked Users</div>
                                <h3 className="mb-0">{summary.blockedUsers}</h3>
                            </div>
                            <div className="icon-box">
                                <FontAwesomeIcon icon={faCircleXmark} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} xl={3}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <Card.Body className="p-4 d-flex justify-content-between">
                            <div>
                                <div className="text-muted small">Loaded Wallet</div>
                                <h3 className="mb-0">{formatCurrency(summary.totalWallet)}</h3>
                            </div>
                            <div className="icon-box">
                                <FontAwesomeIcon icon={faWallet} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row> */}



            <Card className="border-0 shadow-sm rounded-4 mb-4">
                <Card.Body className="p-4">
                    <div>
                        <div className="admin-user-filters mb-3">
                            <Form.Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="admin-user-select"
                            >
                                {statusOptions.map((item) => (
                                    <option key={item.value || "all"} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </Form.Select>

                            <InputGroup className="admin-user-search">
                                <InputGroup.Text className="bg-white">
                                    <FontAwesomeIcon icon={faSearch} className="text-muted" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ boxShadow: "none" }}
                                />
                            </InputGroup>
                        </div>

                    </div>

                    <div className="admin-reports-action-row mt-4">
                        <div className="admin-reports-export-group">
                            <Button
                                type="button"
                                variant="outline-primary"
                                className="fw-semibold"
                                onClick={() => handleExport("csv")}
                                disabled={exportingFormat !== null}
                            >
                                <FontAwesomeIcon
                                    icon={faFileCsv}
                                    // spin={exportingFormat === "csv"}
                                />
                            </Button>

                            <Button
                                type="button"
                                variant="outline-primary"
                                className="fw-semibold"
                                onClick={() => handleExport("excel")}
                                disabled={exportingFormat !== null}
                            >
                                <FontAwesomeIcon
                                    icon={faFileExcel}
                                    // spin={exportingFormat === "excel"}
                                />
                            </Button>

                            <Button
                                type="button"
                                variant="outline-primary"
                                className="fw-semibold"
                                onClick={() => handleExport("pdf")}
                                disabled={exportingFormat !== null}
                            >
                                <FontAwesomeIcon
                                    icon={faFilePdf}
                                    // spin={exportingFormat === "pdf"}
                                />
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>


            <Card className="border-0 shadow-sm rounded-4">
                <Card.Body className="p-4">

                    <div className="user-list-top">
                        <div>
                            <h5 className="mb-1">User List</h5>
                            {/* <small className="text-muted">Review and verify payment proof.</small> */}
                        </div>
                    </div>


                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Sr No</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Mobile</th>
                                    <th>Referral No.</th>
                                    <th>Status</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="text-center text-muted py-5">
                                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                            Loading users...
                                        </td>
                                    </tr>
                                ) : filteredUsers.length > 0 ? (
                                    filteredUsers.map((user, index) => (
                                        <tr key={user.id}>
                                            {/* <td className="fw-semibold">{index + 1}</td> */}
                                            <td className="fw-semibold">{start + index}</td>
                                            <td className="fw-semibold">{user.name}</td>
                                            <td>{user.email || "N/A" || "-"}</td>
                                            <td>{user.mobile || "N/A" || "-"}</td>
                                            <td>{user.referral_number || "N/A" || "-"}</td>
                                            <td>
                                                <Badge
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => handleInlineStatusToggle(user)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            handleInlineStatusToggle(user);
                                                        }
                                                    }}
                                                    className="px-3 py-2 rounded-pill"
                                                    style={{
                                                        ...getStatusBadgeStyle(user.status),
                                                        cursor: "pointer",
                                                        minWidth: "5.5rem",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {formatStatus(user.status)}
                                                </Badge>
                                            </td>
                                            <td className="text-center action-cell">
                                                <div className="d-flex gap-2 justify-content-center flex-wrap action-buttons">
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        className="p-0 text-decoration-none"
                                                        onClick={() => navigate(getAdminUserDetailsPath(user.id))}
                                                    >
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </Button>

                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        className="p-0 text-decoration-none"
                                                        onClick={() => navigate(getAdminEditUserPath(user.id))}
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} />
                                                    </Button>

                                                    
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            className="p-0 text-decoration-none text-danger"
                                                            onClick={() => handleBlockUser(user)}
                                                            title="Block"
                                                        >
                                                            <FontAwesomeIcon icon={faCircleXmark} />
                                                        </Button>

                                                    {/* {String(user.status).toLowerCase() === "blocked" ? (
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            className="p-0 text-decoration-none text-success"
                                                            onClick={() => handleBlockUser(user)}
                                                            title="Activate"
                                                        >
                                                            <FontAwesomeIcon icon={faCircleCheck} />
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            className="p-0 text-decoration-none text-danger"
                                                            onClick={() => handleBlockUser(user)}
                                                            title="Block"
                                                        >
                                                            <FontAwesomeIcon icon={faCircleXmark} />
                                                        </Button>
                                                    )} */}

                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        className="p-0 text-decoration-none text-primary"
                                                        onClick={() => openChangePasswordModal(user)}
                                                        title="Change Password"
                                                    >
                                                        <FontAwesomeIcon icon={faLock} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="text-center text-muted py-5">
                                            No users found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <div className="text-muted small">
                            Page {pagination.current_page} of {pagination.last_page} • Total {pagination.total}
                        </div>

                        <div className="d-flex gap-2">
                            <Button
                                type="button"
                                variant="outline-primary"
                                disabled={pagination.current_page <= 1 || loading}
                                onClick={() => {
                                    const nextPage = Math.max(1, pagination.current_page - 1);
                                    setPagination((prev) => ({ ...prev, current_page: nextPage }));
                                    fetchUsers(nextPage);
                                }}
                            >
                                Prev
                            </Button>

                            <Button
                                type="button"
                                variant="outline-primary"
                                disabled={pagination.current_page >= pagination.last_page || loading}
                                onClick={() => {
                                    const nextPage = Math.min(pagination.last_page, pagination.current_page + 1);
                                    setPagination((prev) => ({ ...prev, current_page: nextPage }));
                                    fetchUsers(nextPage);
                                }}
                            >
                                Next
                            </Button>
                        </div>
                    </div> */}

                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <div className="text-muted small">
                            Showing {start} to {end} of {pagination.total} entries
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {/* <span
                                className={`course-page-btn-wrap ${pagination.current_page <= 1 || loading ? "is-disabled" : ""
                                    }`}
                            > */}
                            <Button
                                type="button"
                                variant="outline-primary"
                                disabled={pagination.current_page <= 1 || loading}
                                onClick={() => {
                                    clearSearchDebounce();
                                    fetchUsers(Math.max(1, pagination.current_page - 1));
                                }}
                                className="course-page-btn"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </Button>
                            {/* </span> */}

                            {getPageItems().map((item, index) =>
                                item === "..." ? (
                                    <span key={`dots-${index}`} className="text-muted px-1">
                                        ...
                                    </span>
                                ) : (
                                    <Button
                                        key={item}
                                        type="button"
                                        variant="outline-primary"
                                        disabled={loading}
                                        onClick={() => {
                                            clearSearchDebounce();
                                            fetchUsers(item);
                                        }}
                                        className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""
                                            }`}
                                    >
                                        {item}
                                    </Button>
                                )
                            )}

                            {/* <span
                                className={`course-page-btn-wrap ${pagination.current_page >= pagination.last_page || loading ? "is-disabled" : ""
                                    }`}
                            > */}
                            <Button
                                type="button"
                                variant="outline-primary"
                                disabled={pagination.current_page >= pagination.last_page || loading}
                                onClick={() => {
                                    clearSearchDebounce();
                                    fetchUsers(Math.min(pagination.last_page, pagination.current_page + 1));
                                }}
                                className="course-page-btn"
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </Button>
                            {/* </span> */}
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Modal show={showPasswordModal} onHide={closeChangePasswordModal} centered backdrop="static">
                <Form onSubmit={handlePasswordSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>Change Password</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <div className="mb-3">
                            <div className="text-muted small mb-2">
                                {selectedPasswordUser
                                    ? `User: ${selectedPasswordUser.name || "N/A" || "-"} (${selectedPasswordUser.email || "N/A" || "-"})`
                                    : ""}
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">
                                Password <span className="text-danger">*</span>
                            </Form.Label>

                            <div
                                className={`password-input-group ${showPasswordError ? "is-invalid-group" : ""}`}
                            >
                                <Form.Control
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    value={passwordForm.password}
                                    onChange={handlePasswordChange}
                                    placeholder="Enter new password"
                                    autoComplete="new-password"
                                    className="password-control"
                                />

                                <Button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </Button>
                            </div>

                            {showPasswordError ? (
                                <div className="text-danger mt-2">
                                    <div className="mb-2">{passwordErrors.password}</div>
                                    {passwordErrors.password === "Password must meet all the rules below." ? (
                                        <ul className="password-rule-image-list mb-0">
                                            {unmetPasswordRules.map((rule) => (
                                                <li key={rule.key}>{rule.label}</li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </div>
                            ) : null}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="fw-semibold">
                                Confirm Password <span className="text-danger">*</span>
                            </Form.Label>

                            <div
                                className={`password-input-group ${showConfirmError ? "is-invalid-group" : ""}`}
                            >
                                <Form.Control
                                    name="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={passwordForm.confirmPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Re-enter password"
                                    autoComplete="new-password"
                                    className="password-control"
                                />

                                <Button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                >
                                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                </Button>
                            </div>

                            {showConfirmError ? (
                                <div className="text-danger mt-2">{passwordErrors.confirmPassword}</div>
                            ) : null}
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button type="button" onClick={closeChangePasswordModal} disabled={passwordSubmitting}>
                            Cancel
                        </Button>
                        <Button type="submit" style={ACTIVE_BUTTON_STYLE} disabled={passwordSubmitting}>
                            {passwordSubmitting ? "Updating..." : "Change Password"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <style>{`
        .admin-user-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 24px 0;
        }

        .admin-user-header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex-wrap: nowrap;
        }

        .admin-user-header-actions .btn {
          white-space: nowrap;
        }

        .admin-user-filters {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .admin-user-search,
        .admin-user-select {
          width: 100%;
        }

        .icon-box {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(44, 96, 186, 0.1);
          color: #005dfb;
          flex: 0 0 auto;
        }

        .password-input-group {
          display: flex;
          width: 100%;
          border: 1px solid #dee2e6;
          border-radius: 0.5rem;
          overflow: hidden;
          background: #fff;
        }

        .password-input-group:focus-within {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
        }

        .password-input-group.is-invalid-group {
          border-color: #dc3545;
        }

        .password-control {
          border: 0 !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          min-height: 46px;
        }

        .password-control:focus {
          box-shadow: none !important;
        }

        .password-toggle-btn {
          min-width: 58px;
          border: 0;
          border-left: 1px solid #dee2e6;
          border-radius: 0;
          background: #e9ecef;
          color: #5f6b7a;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .password-input-group.is-invalid-group .password-toggle-btn {
          border-left-color: #dc3545;
        }

        .password-toggle-btn:hover,
        .password-toggle-btn:focus {
          background: #dee2e6;
          color: #495057;
        }

        .password-rule-image-list {
          padding-left: 1.25rem;
          margin-top: 0.25rem;
        }

        .password-rule-image-list li {
          margin-bottom: 0.35rem;
          color: #ef4444;
        }

        .modal-content {
          border-radius: 18px;
        }

.action-cell {
  white-space: nowrap;
}

.action-buttons {
  flex-wrap: nowrap !important;
  white-space: nowrap;
}

.action-buttons .btn {
  white-space: nowrap;
  flex-shrink: 0;
}


        .admin-reports-action-row {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 1rem;
          flex-wrap: wrap;
        }

        
        .admin-reports-export-group {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: nowrap;
          white-space: nowrap;
        }

        .admin-reports-export-group .btn {
          white-space: nowrap;
          flex: 0 0 auto;
        }

         .user-list-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 12px;
        }


        @media (max-width: 767.98px) {
          .admin-user-header {
            flex-direction: column;
            align-items: stretch;
          }

          .admin-user-header-actions {
            width: 100%;
            overflow-x: auto;
            justify-content: flex-end;
            flex-wrap: nowrap;
            -webkit-overflow-scrolling: touch;
          }

          .admin-user-header-actions .btn {
            width: auto;
            flex: 0 0 auto;
          }

          .admin-user-filters {
            grid-template-columns: 1fr;
          }

          .table th,
          .table td {
            white-space: nowrap;
          }

          .admin-reports-action-row {
            align-items: flex-end;
            justify-content: flex-end;
          }

          
          .admin-reports-export-group {
            width: auto;
          }

          .admin-reports-export-group .btn {
            flex: 0 0 auto;
          }

            .user-list-top {
            flex-direction: column;
            align-items: stretch;
          }

        }

          @media (max-width: 575.98px) {
          .admin-reports-action-row {
            flex-direction: column;
            align-items: flex-end;
          }

          .admin-reports-export-group {
            width: 100%;
            justify-content: flex-end;
            flex-wrap: nowrap;
          }

            }
      `}</style>
        </>
    );
}